/**
 * GlobalTranslator — MutationObserver-based DOM translation plugin.
 *
 * ─── How it works ───────────────────────────────────────────────────────────
 *
 *  1. A single MutationObserver is attached to document.body and watches
 *     EVERY text node that appears in the DOM — across every component.
 *
 *  2. Three translation tables are built from the locale files:
 *
 *       forwardMap   { English → Hindi }   exact whole-string match
 *       reverseMap   { Hindi → English }   exact whole-string match (restore)
 *       fragments    [{ en, lng }, ...]   substring match for mixed content
 *
 *  3. When a text node is seen, translateNode() tries in order:
 *       a. Whole-string exact match in the appropriate map (fast path)
 *       b. Fragment substring replacement (handles "Count: 42" etc.)
 *
 *  4. Vue data is NEVER modified. When Vue reactively updates a node's
 *     value (e.g. "Count: 42" → "Count: 50"), the characterData mutation
 *     fires and the new value is re-translated via fragments.
 *
 *  5. The `_fragments` key in a locale file marks its leaves as fragments
 *     instead of exact-match entries. Example:
 *
 *       en.json: { "_fragments": { "count": "Count: " } }
 *       hi.json: { "_fragments": { "count": "गिनती: " } }
 *
 *     Result: any text node containing "Count: " gets that prefix
 *     replaced with "गिनती: ", leaving the trailing number untouched.
 *
 * ─── Zero component changes ─────────────────────────────────────────────────
 *  Templates unchanged. Scripts unchanged. Data unchanged.
 *  To add a new module: add keys to en.json + hi.json. Nothing else.
 *
 * ─── Infinite-loop prevention ───────────────────────────────────────────────
 *  The _paused flag suppresses observer callbacks during our own DOM writes.
 */

import { loadLocale, localeCache } from './i18n'

// ── Internal state ────────────────────────────────────────────────────────────

var currentLang = 'en'

// English → translated  (exact whole-string match)
var forwardMap = {}

// Translated → English  (used when restoring back to English)
var reverseMap = {}

// Fragments: substring entries for mixed static+dynamic text
//   [{ en: "Count: ", lng: "गिनती: " }, ...]
// Sorted by en.length DESC so longer prefixes win over shorter ones.
var fragments = []

var observer = null
var _paused  = false

// Tags whose text content must never be translated
var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1 }

/**
 * Returns true if `el` is an icon element whose text content is an icon
 * ligature name (e.g. "close", "delete", "arrow_back") rather than user text.
 *
 *   <v-icon>close</v-icon>
 *     ↓ Vuetify renders ↓
 *   <i aria-hidden="true" class="v-icon material-icons">close</i>
 *
 * The Material Icons font turns the literal text "close" into the X glyph.
 * Translating it to "बंद करें" would replace the icon with literal Hindi text.
 *
 * Detected by classes — covers all Material Icons font variants
 * (outlined, round, sharp, two-tone) plus Vuetify's own `v-icon` marker.
 */
function isIconElement (el) {
  if (!el || el.nodeType !== 1 || !el.classList) return false
  if (el.classList.contains('v-icon')) return true
  for (var i = 0; i < el.classList.length; i++) {
    if (el.classList[i].indexOf('material-icons') === 0) return true
  }
  return false
}

/**
 * Returns true if `el` or any ancestor opts out of translation via the
 * `data-no-translate` attribute. Used to skip user-entered data (custom
 * transaction type form names, party names, item names, etc.) that
 * happens to match a key in en.json.
 *
 *   <v-list-tile-title data-no-translate>{{ item.name }}</v-list-tile-title>
 *
 * NOTE: We use a custom data attribute rather than the spec's `translate="no"`
 * because Vue 2 treats `translate` as a reflected boolean DOM property —
 * `translate="no"` gets coerced to `true` and reflected back as
 * `translate="translate"`, defeating the opt-out.
 */
function hasNoTranslate (el) {
  for (var node = el; node && node.nodeType === 1; node = node.parentElement) {
    if (node.hasAttribute && node.hasAttribute('data-no-translate')) return true
  }
  return false
}

// ── Map builder ───────────────────────────────────────────────────────────────

/**
 * Walks the en/hi trees in parallel and populates forwardMap, reverseMap,
 * and fragments. Any subtree under the key "_fragments" is treated as
 * fragment entries instead of exact-match entries.
 */
function buildMaps (lang) {
  forwardMap = {}
  reverseMap = {}
  fragments  = []

  if (lang === 'en') {
    console.log('[translator] language is English — maps cleared')
    return
  }

  var enTree   = localeCache['en'] || {}
  var langTree = localeCache[lang] || {}

  ;(function flatten (enNode, langNode, isFragment) {
    Object.keys(enNode).forEach(function (k) {
      var eVal = enNode[k]
      var lVal = langNode ? langNode[k] : undefined
      var nowFragment = isFragment || k === '_fragments'

      if (typeof eVal === 'string' && typeof lVal === 'string' && eVal !== lVal) {
        if (nowFragment) {
          fragments.push({ en: eVal, lng: lVal })
        } else {
          forwardMap[eVal] = lVal
          reverseMap[lVal] = eVal
        }
      } else if (eVal !== null && typeof eVal === 'object') {
        flatten(eVal, lVal || {}, nowFragment)
      }
    })
  })(enTree, langTree, false)

  // Longer fragments matched first so "Bill Amount: " wins over "Bill "
  fragments.sort(function (a, b) { return b.en.length - a.en.length })

  console.log(
    '[translator] built —',
    Object.keys(forwardMap).length, 'exact entries,',
    fragments.length, 'fragments'
  )
}

// ── Text node translation ─────────────────────────────────────────────────────

function translateNode (node) {
  var raw = node.nodeValue
  if (!raw || !raw.trim()) return

  // Skip Material Icons / Vuetify v-icon ligatures — their text IS the icon
  if (isIconElement(node.parentElement)) return

  // Skip user-entered data marked with translate="no"
  if (hasNoTranslate(node.parentElement)) return

  var trimmed     = raw.trim()
  var replacement = raw

  if (currentLang === 'en') {
    // Restore: Hindi (or whatever) → English
    if (reverseMap[trimmed]) {
      replacement = raw.replace(trimmed, reverseMap[trimmed])
    } else if (fragments.length) {
      // Reverse fragment substitution for mixed content
      var workingR = raw
      var changedR = false
      for (var i = 0; i < fragments.length; i++) {
        var f = fragments[i]
        if (workingR.indexOf(f.lng) !== -1) {
          workingR = workingR.split(f.lng).join(f.en)
          changedR = true
        }
      }
      if (changedR) replacement = workingR
    }
  } else {
    // Translate: English → current language
    if (forwardMap[trimmed]) {
      replacement = raw.replace(trimmed, forwardMap[trimmed])
    } else if (fragments.length) {
      // Forward fragment substitution for mixed content
      var workingF = raw
      var changedF = false
      for (var j = 0; j < fragments.length; j++) {
        var ff = fragments[j]
        if (workingF.indexOf(ff.en) !== -1) {
          workingF = workingF.split(ff.en).join(ff.lng)
          changedF = true
        }
      }
      if (changedF) replacement = workingF
    }
  }

  if (replacement === raw) return

  _paused = true
  node.nodeValue = replacement
  _paused = false

  console.log('[translator]', JSON.stringify(raw.trim()), '→', JSON.stringify(replacement.trim()))
}

// ── DOM walker ────────────────────────────────────────────────────────────────

function walkElement (el) {
  if (!el) return
  el.childNodes.forEach(function (node) {
    if (node.nodeType === 3) {                                          // TEXT_NODE
      translateNode(node)
    } else if (node.nodeType === 1 && !SKIP_TAGS[node.tagName]) {       // ELEMENT_NODE
      if (isIconElement(node)) return                                   // don't descend into icon
      if (node.hasAttribute && node.hasAttribute('data-no-translate')) return  // opt-out subtree
      walkElement(node)
    }
  })
}

// ── MutationObserver ──────────────────────────────────────────────────────────

// Vuetify 1.1.9's detachable mixin (used by <v-menu>, <v-select>, <v-autocomplete>,
// <v-dialog>) moves its content element to [data-app] at mount time via
// initDetach(). The portal container (.v-menu__content / .v-dialog__content)
// loses its original ancestor chain, so a later `hasNoTranslate(contentEl)`
// never sees the opt-out that was set on the activator's form.
//
// Fix: when a portal container is added anywhere in the DOM, find its owning
// in-place Vuetify component (<v-menu> / <v-dialog>) via Vue 2's `__vue__`
// hook and the owner's `$refs.content` back-reference. The owner is still in
// place inside the activator's subtree, so its ancestry is intact. If that
// ancestry includes `data-no-translate`, stamp the attribute onto the portal
// container so the walker (and subsequent child mutations) skip it.
function findOwningPortalHost (contentEl) {
  // VMenu root is `.v-menu`; VDialog root is `.v-dialog__container`.
  var candidates = document.querySelectorAll('.v-menu, .v-dialog__container')
  for (var i = 0; i < candidates.length; i++) {
    var host = candidates[i]
    var vueInst = host.__vue__
    if (vueInst && vueInst.$refs && vueInst.$refs.content === contentEl) {
      return host
    }
  }
  return null
}

function isPortalContainer (node) {
  if (!node || node.nodeType !== 1 || !node.classList) return false
  return node.classList.contains('v-menu__content') ||
         node.classList.contains('v-dialog__content')
}

function maybeInheritFromHost (node) {
  if (!isPortalContainer(node)) return false
  if (node.hasAttribute('data-no-translate')) return true
  var host = findOwningPortalHost(node)
  if (host && hasNoTranslate(host)) {
    node.setAttribute('data-no-translate', '')
    return true
  }
  return false
}

function startObserver () {
  if (!document.body) {
    console.warn('[translator] document.body not ready — observer not started')
    return
  }

  observer = new MutationObserver(function (mutations) {
    if (_paused) return

    mutations.forEach(function (mutation) {
      if (mutation.type === 'characterData') {
        translateNode(mutation.target)
      } else if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 3) {
            translateNode(node)
          } else if (node.nodeType === 1 && !SKIP_TAGS[node.tagName]) {
            if (isIconElement(node)) return                              // skip icon subtree

            // Portal content added (e.g. moved to [data-app] by initDetach):
            // inherit `data-no-translate` from its in-place owning host.
            maybeInheritFromHost(node)

            // Likewise, if the added subtree CONTAINS portal containers that
            // haven't yet been detached — stamp them before Vuetify moves them.
            if (node.querySelectorAll) {
              var portals = node.querySelectorAll('.v-menu__content, .v-dialog__content')
              for (var i = 0; i < portals.length; i++) {
                maybeInheritFromHost(portals[i])
              }
            }

            if (hasNoTranslate(node)) return                             // opt-out subtree (checks node + all ancestors)
            walkElement(node)
          }
        })
      }
    })
  })

  observer.observe(document.body, {
    childList:     true,  // new nodes added anywhere in the tree
    subtree:       true,  // watch the entire document
    characterData: true   // Vue reactive value updates
  })

  console.log('[translator] MutationObserver attached to document.body')
}

// ── Language application ──────────────────────────────────────────────────────

async function applyLanguage (lang) {
  console.log('[translator] ── applying language:', lang, '──')

  await loadLocale('en')
  if (lang !== 'en') await loadLocale(lang)

  // Three cases:
  //   en → other:  buildMaps(other); walk (English → other)
  //   other → en:  walk first (restore via reverseMap); then buildMaps('en')
  //   other → other (e.g., hi → hinglish):
  //     must FIRST restore to English using the CURRENT (old) reverseMap,
  //     THEN switch maps to the new language and translate forward.
  //     Otherwise the DOM holds old-language text that has no entry in the
  //     new forwardMap (which is keyed by English).

  if (lang === 'en') {
    currentLang = 'en'
    stampDetachedPortals()
    if (document.body) walkElement(document.body)
    buildMaps('en')
  } else if (currentLang !== 'en') {
    // Non-en → non-en: restore to en first, then forward to new lang.
    var prevLang = currentLang
    currentLang = 'en'
    stampDetachedPortals()
    if (document.body) walkElement(document.body)
    if (prevLang !== lang) {
      buildMaps(lang)
      currentLang = lang
      stampDetachedPortals()
      if (document.body) walkElement(document.body)
    } else {
      buildMaps('en')
    }
  } else {
    buildMaps(lang)
    currentLang = lang
    stampDetachedPortals()
    if (document.body) walkElement(document.body)
  }
}

// Stamp `data-no-translate` on every detached portal container whose owning
// in-place host sits inside a no-translate subtree. Called before each
// full-document walk so hidden/pre-detached dropdowns are correctly opted out.
function stampDetachedPortals () {
  if (!document.body) return
  var portals = document.querySelectorAll('.v-menu__content, .v-dialog__content')
  for (var i = 0; i < portals.length; i++) {
    maybeInheritFromHost(portals[i])
  }
}

// ── Vue Plugin ────────────────────────────────────────────────────────────────

var GlobalTranslator = {
  install: function (Vue) {
    Vue.mixin({
      mounted: function () {
        if (this !== this.$root) return            // run once on root only

        console.log('[translator] root Vue instance mounted — initialising')
        startObserver()

        var savedLang = this.$root.selectedLanguage || 'en'
        if (savedLang !== 'en') {
          applyLanguage(savedLang)
        }

        this.$watch('selectedLanguage', function (newLang) {
          applyLanguage(newLang || 'en')
        })
      }
    })
  }
}

export { forwardMap }
export default GlobalTranslator



// WEBPACK FOOTER //
// ./src/plugins/global_translator.js